import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Megaphone, Rocket, Loader2, Lock, Users, TrendingUp, Link2, Bookmark, CheckCircle2, Clock, Radio, XCircle, Search, Youtube, Upload, Image as ImageIcon, Share2 } from "lucide-react";

const PLATFORMS = [
  { id: "tiktok", name: "TikTok" },
  { id: "instagram", name: "Instagram" },
  { id: "youtube", name: "YouTube" },
  { id: "facebook", name: "Facebook" },
];

const fmtM = (n) => {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n ?? 0);
};

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

const STATUS = {
  matched: { label: "Matched", icon: Users, cls: "text-primary bg-primary/10 border-primary/30" },
  shared: { label: "Shared with creators", icon: Clock, cls: "text-amber-400 bg-amber-400/10 border-amber-400/30" },
  live: { label: "Live shoutout", icon: Radio, cls: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30" },
  declined: { label: "Not a fit", icon: XCircle, cls: "text-muted-foreground bg-white/5 border-white/10" },
};

const StatusBadge = ({ status }) => {
  const s = STATUS[status] || STATUS.matched;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${s.cls}`} data-testid={`amplify-status-${status}`}>
      <Icon className="w-3.5 h-3.5" /> {s.label}
    </span>
  );
};

const CreatorAvatar = ({ c }) => (
  <div className="flex items-center gap-3" data-testid="amplify-creator">
    <div className="w-10 h-10 rounded-full bg-secondary/15 border border-secondary/30 flex items-center justify-center text-sm font-bold text-secondary shrink-0">{initials(c.name)}</div>
    <div className="min-w-0">
      <div className="font-semibold text-sm truncate">{c.name}</div>
      <div className="text-xs text-muted-foreground truncate">{c.handle} · {fmtM(c.followers)} followers</div>
    </div>
  </div>
);

const ShareKit = ({ req, best }) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(() => (req.matched_creators || []).map((c) => c.handle));
  const [result, setResult] = useState(req.share_token ? { share_url: `${process.env.REACT_APP_BACKEND_URL}/api/amplify/share/${req.share_token}` } : null);
  const [busy, setBusy] = useState(false);
  const [style, setStyle] = useState("friendly");
  const toggle = (h) => setSel((s) => (s.includes(h) ? s.filter((x) => x !== h) : [...s, h]));
  const generate = async () => {
    setBusy(true);
    try {
      const chosen = (req.matched_creators || []).filter((c) => sel.includes(c.handle));
      const { data } = await api.post(`/amplify/${req.id}/share`, { selected_creators: chosen });
      setResult(data);
      toast.success("Share kit ready!");
    } catch { toast.error("Couldn't create share kit"); }
    finally { setBusy(false); }
  };
  const link = result?.share_url ? `${result.share_url}?s=${style}` : "";
  const chosenNames = (req.matched_creators || []).filter((c) => sel.includes(c.handle)).map((c) => c.name).join(", ");
  const messages = {
    friendly: `Hi! I'd love for you to check out my video "${req.title}" for a possible shoutout or collab. Watch it here: ${link}\n\nThanks so much! 🙌`,
    professional: `Hello,\n\nI'm reaching out about a potential collaboration. I've created a video, "${req.title}", that I believe would resonate with your audience. You can view it here: ${link}\n\nI'd welcome the chance to discuss a shoutout or partnership.\n\nBest regards`,
    short: `Hey! Would you shout out my video "${req.title}"? ${link} 🙏`,
  };
  const message = messages[style];
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied!"); };
  return (
    <div className="mt-4 border-t border-white/5 pt-3">
      <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)} className="rounded-full border-white/15 text-xs" data-testid={`amplify-share-toggle-${req.id}`}>
        <Share2 className="w-3.5 h-3.5 mr-1.5" /> Share with creators
      </Button>
      {req.share_views > 0 && <span className="ml-3 text-xs text-muted-foreground" data-testid={`amplify-share-views-${req.id}`}>👁 {req.share_views} {req.share_views === 1 ? "open" : "opens"}</span>}
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {(req.matched_creators || []).map((c) => (
              <label key={c.handle} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={sel.includes(c.handle)} onChange={() => toggle(c.handle)} data-testid={`amplify-share-check-${req.id}`} className="accent-[#00F2FE]" />
                <span className="truncate">{c.name}</span>
              </label>
            ))}
          </div>
          <Button size="sm" onClick={generate} disabled={busy || sel.length === 0} className="rounded-full bg-primary text-background text-xs font-bold" data-testid={`amplify-share-generate-${req.id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Generate share kit"}
          </Button>
          {link && (
            <div className="space-y-2" data-testid={`amplify-share-result-${req.id}`}>
              <div className="flex gap-2">
                <Input readOnly value={link} onFocus={(e) => e.target.select()} className="h-9 bg-background border-white/10 text-xs" data-testid={`amplify-share-link-${req.id}`} />
                <Button size="sm" variant="outline" onClick={() => copy(link)} className="rounded-lg border-white/15 text-xs shrink-0">Copy link</Button>
              </div>
              <Textarea readOnly value={message} rows={3} className="bg-background border-white/10 text-xs" />
              <div className="flex items-center gap-1.5 flex-wrap">
                {["friendly", "professional", "short"].map((s) => (
                  <button key={s} type="button" onClick={() => setStyle(s)} data-testid={`amplify-style-${s}`}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize border transition-colors ${style === s ? "bg-primary text-background border-primary" : "border-white/15 text-muted-foreground hover:text-foreground"}`}>{s}{best === s ? " ★" : ""}</button>
                ))}
                {best && <span className="text-[11px] text-primary self-center">★ {best} gets the most opens</span>}
              </div>
              <div className="flex items-center gap-3">
                <Button size="sm" variant="outline" onClick={() => copy(message)} className="rounded-full border-white/15 text-xs">Copy message</Button>
                <a href={`mailto:?subject=${encodeURIComponent("Collab / shoutout request")}&body=${encodeURIComponent(message)}`} className="text-xs text-primary underline">Open email</a>
              </div>
              <p className="text-[11px] text-muted-foreground">Sharing with: {chosenNames || "—"}. Your CloutRise team also sees your selected creators to help arrange it.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Amplify = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [plan, setPlan] = useState(null);
  const [requests, setRequests] = useState([]);
  const [network, setNetwork] = useState([]);
  const [netQuery, setNetQuery] = useState("");
  const [library, setLibrary] = useState([]);
  const [usage, setUsage] = useState(null);
  const [discoverQ, setDiscoverQ] = useState("");
  const [discoverResults, setDiscoverResults] = useState(null);
  const [discovering, setDiscovering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [bestTemplate, setBestTemplate] = useState(null);
  const [myUploads, setMyUploads] = useState([]);
  const [thumbUrl, setThumbUrl] = useState("");
  const [capturing, setCapturing] = useState(false);
  const previewRef = useRef(null);
  const autoThumbDone = useRef(false);
  const fileUrl = (p) => `${process.env.REACT_APP_BACKEND_URL}/api/amplify/files/${p}`;
  const [form, setForm] = useState({ platform: "tiktok", source: "link", video_url: "", library_id: "", title: "", note: "", target: null });

  const loadRequests = () =>
    api.get("/amplify/requests").then((r) => { setRequests(r.data.requests); setEligible(r.data.eligible); setUsage(r.data.usage); setPlan(r.data.plan); }).catch(() => {});

  const loadNetwork = (platform, q = "") =>
    api.get(`/amplify/network?platform=${platform}&q=${encodeURIComponent(q)}`).then((r) => setNetwork(r.data.creators)).catch(() => {});

  const loadUploads = () =>
    api.get("/amplify/uploads").then((r) => setMyUploads(r.data.uploads || [])).catch(() => {});

  const runDiscover = async () => {
    if (!discoverQ.trim()) return;
    setDiscovering(true);
    try {
      const { data } = await api.get(`/amplify/discover?q=${encodeURIComponent(discoverQ.trim())}&min_followers=1000000`);
      setDiscoverResults(data.creators);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Discovery unavailable");
      setDiscoverResults([]);
    } finally {
      setDiscovering(false);
    }
  };

  useEffect(() => {
    Promise.all([loadRequests(), loadNetwork(form.platform)]).finally(() => setLoading(false));
    api.get("/library").then((r) => setLibrary(r.data.items || [])).catch(() => {});
    loadUploads();
    api.get("/amplify/template-stats").then((r) => setBestTemplate(r.data.best)).catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => { loadNetwork(form.platform, netQuery); }, [form.platform]); // eslint-disable-line

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = { platform: form.platform, source: form.source, title: form.title, note: form.note };
      if (form.source === "library") payload.library_id = form.library_id;
      else payload.video_url = form.video_url;
      if (form.thumbnail_url) payload.thumbnail_url = form.thumbnail_url;
      if (form.target) {
        payload.target_name = form.target.name;
        payload.target_handle = form.target.handle;
        payload.target_channel_id = form.target.channel_id;
        payload.target_followers = form.target.followers;
        payload.target_platform = form.target.platform;
      }
      await api.post("/amplify/request", payload);
      toast.success("Submitted! We've matched you with creators in your niche.");
      setForm({ ...form, video_url: "", library_id: "", title: "", note: "", target: null, thumbnail_url: null });
      setUploadedName("");
      setPreviewUrl("");
      setThumbUrl("");
      await loadRequests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const outOfQuota = usage && usage.quota != null && usage.remaining <= 0;
  const handleFile = async (f) => {
    if (!f) return;
    if (!["video/mp4", "video/quicktime", "video/webm"].includes(f.type)) {
      toast.error("Please choose an MP4, MOV or WEBM video."); return;
    }
    if (f.size > 200 * 1024 * 1024) { toast.error("Video is too large (max 200MB)."); return; }
    setUploading(true); setUploadPct(0); setUploadedName(""); setThumbUrl(""); autoThumbDone.current = false;
    try { setPreviewUrl(URL.createObjectURL(f)); } catch { /* ignore */ }
    try {
      const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const CH = 4 * 1024 * 1024;
      const total = Math.ceil(f.size / CH);
      let done = 0;
      const sendChunk = (i) => {
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("index", i);
        fd.append("total", total);
        fd.append("content_type", f.type);
        fd.append("file", f.slice(i * CH, (i + 1) * CH), f.name);
        return api.post("/amplify/upload/chunk", fd).then(() => { done += 1; setUploadPct(Math.round((done / total) * 100)); });
      };
      // Upload chunks with limited concurrency (4 at a time) for speed
      const CONC = 4;
      const queue = Array.from({ length: total }, (_, i) => i);
      while (queue.length) await Promise.all(queue.splice(0, CONC).map(sendChunk));
      const { data } = await api.post("/amplify/upload/complete", { upload_id: uploadId, filename: f.name, content_type: f.type });
      setForm((prev) => ({ ...prev, video_url: fileUrl(data.path), title: prev.title || (data.name || "").replace(/\.[^.]+$/, "") }));
      setUploadedName(data.name);
      toast.success("Video uploaded!");
      loadUploads();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const captureThumbnail = async (silent = false) => {
    const v = previewRef.current;
    if (!v) return;
    if (!silent) setCapturing(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth || 640;
      canvas.height = v.videoHeight || 360;
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("capture failed");
      const fd = new FormData();
      fd.append("file", blob, "thumbnail.jpg");
      const { data } = await api.post("/amplify/thumbnail", fd);
      const url = fileUrl(data.path);
      setThumbUrl(url);
      setForm((prev) => ({ ...prev, thumbnail_url: url }));
      if (!silent) toast.success("Thumbnail captured!");
    } catch (err) {
      if (!silent) toast.error("Couldn't capture this frame — try a different moment.");
    } finally {
      if (!silent) setCapturing(false);
    }
  };

  // Auto-grab a default thumbnail once the preview is ready
  const onPreviewLoaded = () => {
    const v = previewRef.current;
    if (!v || autoThumbDone.current) return;
    try { v.currentTime = Math.min(1, (v.duration || 2) / 3); } catch { /* ignore */ }
  };
  const onPreviewSeeked = () => {
    if (autoThumbDone.current || thumbUrl) return;
    autoThumbDone.current = true;
    captureThumbnail(true);
  };

  const reuseUpload = (u) => {
    const url = fileUrl(u.path);
    autoThumbDone.current = false;
    setThumbUrl("");
    setForm((prev) => ({ ...prev, source: "upload", video_url: url, thumbnail_url: null, title: prev.title || (u.name || "").replace(/\.[^.]+$/, "") }));
    setPreviewUrl(url);
    setUploadedName(u.name);
    toast.success(`Reusing ${u.name}`);
  };

  const deleteUpload = async (u, e) => {
    e.stopPropagation();
    try {
      await api.post("/amplify/uploads/delete", { path: u.path });
      setMyUploads((prev) => prev.filter((x) => x.path !== u.path));
      toast.success("Upload removed");
    } catch {
      toast.error("Couldn't remove upload");
    }
  };

  const uploadFile = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); };

  const canSubmit = !outOfQuota && (form.source === "library" ? !!form.library_id : (form.video_url.trim() && form.title.trim()));

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-6xl mx-auto">
        <div className="mb-8 flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0">
            <Megaphone className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">Amplify</h1>
            <p className="text-muted-foreground mt-1 max-w-2xl">Submit your best videos to get matched with large creators in your niche for shoutouts and cross-promotion — putting your content in front of millions.</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : !eligible ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-primary/30 bg-primary/5 p-10 text-center max-w-xl mx-auto" data-testid="amplify-locked">
            <div className="w-14 h-14 rounded-2xl bg-primary/15 border border-primary/30 flex items-center justify-center mx-auto mb-5">
              <Lock className="w-7 h-7 text-primary" />
            </div>
            <h2 className="font-heading text-2xl font-black">Amplify is a premium perk</h2>
            <p className="text-muted-foreground mt-3">Creator shoutouts and network cross-promotion are included on the <b className="text-foreground">Growth</b> and <b className="text-foreground">Pro</b> plans. Upgrade to unlock it.</p>
            <Button onClick={() => navigate("/billing")} className="mt-6 rounded-full bg-primary text-background font-bold hover:bg-primary/90" data-testid="amplify-upgrade-btn">
              <Rocket className="w-4 h-4 mr-1.5" /> Upgrade to unlock
            </Button>
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Submit + submissions */}
            <div className="lg:col-span-3 space-y-8">
              <div className="rounded-2xl border border-border/50 bg-card p-6" data-testid="amplify-submit-card">
                <h2 className="font-heading text-xl font-bold mb-5 flex items-center gap-2"><Rocket className="w-5 h-5 text-primary" /> Submit a video to amplify</h2>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm">Platform</Label>
                    <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                      <SelectTrigger className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="amplify-platform-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-1 rounded-full border border-white/10 bg-background p-1 w-fit" data-testid="amplify-source-toggle">
                    <button onClick={() => setForm({ ...form, source: "link" })} data-testid="amplify-source-link"
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${form.source === "link" ? "bg-primary text-background" : "text-muted-foreground hover:text-foreground"}`}>
                      <Link2 className="w-3.5 h-3.5" /> Paste a link
                    </button>
                    <button onClick={() => setForm({ ...form, source: "library" })} data-testid="amplify-source-library"
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${form.source === "library" ? "bg-primary text-background" : "text-muted-foreground hover:text-foreground"}`}>
                      <Bookmark className="w-3.5 h-3.5" /> From Library
                    </button>
                    <button onClick={() => setForm({ ...form, source: "upload" })} data-testid="amplify-source-upload"
                      className={`px-4 py-1.5 rounded-full text-sm font-semibold flex items-center gap-1.5 transition-colors ${form.source === "upload" ? "bg-primary text-background" : "text-muted-foreground hover:text-foreground"}`}>
                      <Upload className="w-3.5 h-3.5" /> Upload
                    </button>
                  </div>

                  {form.source === "link" ? (
                    <>
                      <div>
                        <Label className="text-sm">Video link</Label>
                        <Input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://..."
                          className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" data-testid="amplify-video-url" />
                      </div>
                      <div>
                        <Label className="text-sm">Title</Label>
                        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What's this video about?"
                          className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" data-testid="amplify-title" />
                      </div>
                    </>
                  ) : form.source === "upload" ? (
                    <>
                      <div>
                        <Label className="text-sm">Upload a video from your device</Label>
                        <label htmlFor="amplify-file" onDragOver={(e) => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
                          className={`mt-1.5 flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed py-8 px-4 cursor-pointer transition-colors ${dragOver ? "border-primary bg-primary/10" : "border-white/20 bg-background hover:border-primary/40"}`} data-testid="amplify-upload-drop">
                          {uploading ? (
                            <>
                              <Loader2 className="w-6 h-6 text-primary animate-spin" />
                              <span className="text-sm text-foreground">Uploading… {uploadPct}%</span>
                              <div className="w-40 h-1.5 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${uploadPct}%` }} data-testid="amplify-upload-progress" /></div>
                            </>
                          ) : uploadedName ? (
                            <><CheckCircle2 className="w-6 h-6 text-primary" /><span className="text-sm text-foreground">{uploadedName}</span><span className="text-xs text-muted-foreground">Drop or tap to replace</span></>
                          ) : (
                            <><Upload className="w-6 h-6 text-muted-foreground" /><span className="text-sm font-medium">{dragOver ? "Drop your video here" : "Drag & drop or choose a video file"}</span><span className="text-xs text-muted-foreground">MP4, MOV or WEBM · up to 200MB</span></>
                          )}
                          <input id="amplify-file" type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={uploadFile} data-testid="amplify-file-input" />
                        </label>
                        {previewUrl && (
                          <div className="mt-3 space-y-2">
                            <video ref={previewRef} src={previewUrl} controls crossOrigin="anonymous" onLoadedData={onPreviewLoaded} onSeeked={onPreviewSeeked} className="w-full max-h-56 rounded-xl border border-white/10 bg-black" data-testid="amplify-upload-preview" />
                            <div className="flex items-center gap-3 flex-wrap">
                              <Button type="button" size="sm" variant="outline" onClick={() => captureThumbnail()} disabled={capturing}
                                className="rounded-full border-white/15 text-xs" data-testid="amplify-capture-thumb">
                                {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><ImageIcon className="w-3.5 h-3.5 mr-1.5" /> {thumbUrl ? "Change thumbnail to current frame" : "Use current frame as thumbnail"}</>}
                              </Button>
                              {thumbUrl && <img src={thumbUrl} alt="thumbnail" className="w-16 h-10 object-cover rounded border border-primary/40" data-testid="amplify-thumb-preview" />}
                            </div>
                            <p className="text-[11px] text-muted-foreground">A thumbnail is auto-picked for you — scrub to a better moment and capture it to change.</p>
                          </div>
                        )}
                        {myUploads.length > 0 && (
                          <div className="mt-4" data-testid="amplify-my-uploads">
                            <Label className="text-xs text-muted-foreground">Or reuse a previous upload</Label>
                            <div className="mt-2 flex gap-2 overflow-x-auto pb-1">
                              {myUploads.map((u) => (
                                <div key={u.path} className="relative shrink-0 group">
                                  <button type="button" onClick={() => reuseUpload(u)} data-testid="amplify-reuse-upload"
                                    className="max-w-[160px] text-left rounded-lg border border-white/10 bg-background pl-3 pr-7 py-2 hover:border-primary/40 transition-colors">
                                    <div className="text-xs font-medium truncate flex items-center gap-1.5"><Upload className="w-3 h-3 text-primary" /> {u.name}</div>
                                    <div className="text-[10px] text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</div>
                                  </button>
                                  <button type="button" onClick={(e) => deleteUpload(u, e)} data-testid="amplify-delete-upload"
                                    className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-muted-foreground hover:text-secondary hover:bg-white/10" title="Remove">
                                    <XCircle className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <Label className="text-sm">Title</Label>
                        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What's this video about?"
                          className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" data-testid="amplify-title-upload" />
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label className="text-sm">Pick from your Library</Label>
                      {library.length === 0 ? (
                        <p className="text-sm text-muted-foreground mt-2">No saved items yet. Save content from the AI Studio, or paste a link instead.</p>
                      ) : (
                        <Select value={form.library_id} onValueChange={(v) => setForm({ ...form, library_id: v })}>
                          <SelectTrigger className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="amplify-library-select"><SelectValue placeholder="Choose a saved item" /></SelectTrigger>
                          <SelectContent>{library.map((it) => <SelectItem key={it.id} value={it.id}>{it.title || it.type}</SelectItem>)}</SelectContent>
                        </Select>
                      )}
                    </div>
                  )}

                  <div>
                    <Label className="text-sm">Note to the network <span className="text-muted-foreground font-normal">(optional)</span></Label>
                    <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Anything the creators should know?"
                      className="mt-1.5 bg-transparent border-white/10 focus:border-primary" rows={2} data-testid="amplify-note" />
                  </div>

                  {form.target && (
                    <div className="flex items-center justify-between gap-2 rounded-lg px-3 py-2 border border-secondary/30 bg-secondary/10" data-testid="amplify-target-chip">
                      <span className="text-sm text-foreground truncate">🎯 Requesting shoutout from <b>{form.target.name}</b> ({fmtM(form.target.followers)})</span>
                      <button onClick={() => setForm({ ...form, target: null })} className="text-muted-foreground hover:text-foreground shrink-0" data-testid="amplify-target-clear"><XCircle className="w-4 h-4" /></button>
                    </div>
                  )}
                  {usage && usage.quota != null && (
                    <div className={`text-sm rounded-lg px-3 py-2 border ${outOfQuota ? "text-amber-400 bg-amber-400/10 border-amber-400/30" : "text-muted-foreground bg-white/5 border-white/10"}`} data-testid="amplify-quota">
                      {outOfQuota
                        ? <>You've used all <b>{usage.quota}</b> submissions this month. Resets {usage.resets_on}. <button onClick={() => navigate("/billing")} className="text-primary font-semibold underline" data-testid="amplify-quota-upgrade">Upgrade to Pro</button> for more.</>
                        : <><b className="text-foreground">{usage.remaining}</b> of {usage.quota} submissions left this month{plan === "growth" && <span className="text-primary"> · Pro members get 15 <button onClick={() => navigate("/plans")} className="underline font-semibold" data-testid="amplify-nudge-upgrade">Upgrade</button></span>}</>}
                    </div>
                  )}
                  <Button onClick={submit} disabled={!canSubmit || submitting} className="w-full rounded-full bg-primary text-background font-bold hover:bg-primary/90" data-testid="amplify-submit-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Megaphone className="w-4 h-4 mr-1.5" /> Submit for amplification</>}
                  </Button>
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-bold mb-4">Your submissions</h2>
                {requests.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-muted-foreground" data-testid="amplify-empty">
                    No submissions yet. Send your first video above to get matched with creators.
                  </div>
                ) : (
                  <div className="space-y-4">
                    {requests.map((r) => (
                      <motion.div key={r.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                        className="rounded-2xl border border-border/50 bg-card p-5" data-testid={`amplify-request-${r.id}`}>
                        <div className="flex items-start justify-between gap-4 mb-4">
                          <div className="flex items-start gap-3 min-w-0">
                            {r.thumbnail_url && <img src={r.thumbnail_url} alt="" className="w-20 h-12 object-cover rounded-lg border border-white/10 shrink-0" data-testid={`amplify-card-thumb-${r.id}`} />}
                            <div className="min-w-0">
                              <div className="font-heading font-bold truncate">{r.title}</div>
                              <div className="text-xs text-muted-foreground capitalize">{r.platform} · submitted {new Date(r.created_at).toLocaleDateString()}</div>
                            </div>
                          </div>
                          <StatusBadge status={r.status} />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4 mb-4">
                          <div className="rounded-xl bg-background border border-white/5 p-4">
                            <div className="text-xs text-muted-foreground flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-primary" /> Projected reach</div>
                            <div className="font-heading text-2xl font-black text-primary mt-1">{fmtM(r.projected_reach)}</div>
                          </div>
                          <div className="rounded-xl bg-background border border-white/5 p-4">
                            <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-secondary" /> Est. new followers</div>
                            <div className="font-heading text-2xl font-black mt-1">+{fmtM(r.projected_new_followers)}</div>
                          </div>
                        </div>
                        {r.requested_target && (
                          <div className="mb-3 text-sm flex items-center gap-2" data-testid={`amplify-req-target-${r.id}`}>
                            <span className="text-muted-foreground">🎯 Requested shoutout:</span>
                            <b>{r.requested_target.name}</b>
                            <span className="text-xs text-muted-foreground">({fmtM(r.requested_target.followers)})</span>
                          </div>
                        )}
                        <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Matched creators</div>
                        <div className="grid sm:grid-cols-3 gap-3">
                          {r.matched_creators.map((c) => <CreatorAvatar key={c.handle} c={c} />)}
                        </div>
                        {r.admin_note && <div className="mt-4 text-sm text-muted-foreground border-t border-white/5 pt-3"><b className="text-foreground">Update from our team:</b> {r.admin_note}</div>}
                        <ShareKit req={r} best={bestTemplate} />
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Network roster */}
            <div className="lg:col-span-2">
              <div className="rounded-2xl border border-border/50 bg-card p-6 sticky top-6" data-testid="amplify-network">
                <h2 className="font-heading text-xl font-bold mb-1 flex items-center gap-2"><Users className="w-5 h-5 text-secondary" /> Network creators</h2>
                <p className="text-xs text-muted-foreground mb-4 capitalize">Top {PLATFORMS.find((p) => p.id === form.platform)?.name} creators you can be matched with</p>
                <div className="relative mb-4">
                  <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={netQuery} onChange={(e) => { setNetQuery(e.target.value); loadNetwork(form.platform, e.target.value); }}
                    placeholder="Search by name or niche..." className="pl-9 h-10 bg-transparent border-white/10 focus:border-primary" data-testid="amplify-network-search" />
                </div>
                <div className="space-y-4 max-h-[320px] overflow-y-auto pr-1">
                  {network.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No creators match that search.</p>
                    : network.map((c) => (
                    <div key={c.handle} className="flex items-center justify-between gap-3">
                      <CreatorAvatar c={c} />
                      <Button size="sm" variant="outline" onClick={() => { setForm({ ...form, target: { name: c.name, handle: c.handle, channel_id: null, followers: c.followers, platform: c.platform } }); toast.success(`Targeting ${c.name} for your next submission`); }}
                        className="rounded-full border-white/15 text-xs h-7 px-3 shrink-0" data-testid={`amplify-target-roster-${c.handle}`}>Target</Button>
                    </div>
                  ))}
                </div>

                <div className="mt-5 pt-5 border-t border-white/5">
                  <div className="flex items-center gap-2 text-sm font-semibold mb-1"><Youtube className="w-4 h-4 text-secondary" /> Discover real YouTube creators</div>
                  <p className="text-[11px] text-muted-foreground mb-3">Search live YouTube channels with 1M+ subscribers.</p>
                  <div className="flex gap-2">
                    <Input value={discoverQ} onChange={(e) => setDiscoverQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runDiscover()}
                      placeholder="e.g. fitness, gaming..." className="h-10 bg-transparent border-white/10 focus:border-primary" data-testid="amplify-discover-input" />
                    <Button onClick={runDiscover} disabled={discovering || !discoverQ.trim()} className="rounded-lg bg-secondary text-white font-bold shrink-0" data-testid="amplify-discover-btn">
                      {discovering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    </Button>
                  </div>
                  {discoverResults != null && (
                    <div className="mt-4 space-y-3 max-h-[280px] overflow-y-auto pr-1" data-testid="amplify-discover-results">
                      {discoverResults.length === 0 ? <p className="text-sm text-muted-foreground text-center py-2">No 1M+ channels found for that search.</p>
                        : discoverResults.map((c) => (
                        <div key={c.channel_id} className="flex items-center gap-3">
                          {c.thumbnail ? <img src={c.thumbnail} alt={c.name} className="w-10 h-10 rounded-full border border-white/10 shrink-0" />
                            : <div className="w-10 h-10 rounded-full bg-secondary/15 border border-secondary/30 flex items-center justify-center text-sm font-bold text-secondary shrink-0">{initials(c.name)}</div>}
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-sm truncate">{c.name}</div>
                            <div className="text-xs text-muted-foreground truncate">{fmtM(c.followers)} subscribers</div>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => { setForm({ ...form, target: { name: c.name, handle: c.handle, channel_id: c.channel_id, followers: c.followers } }); toast.success(`Targeting ${c.name} for your next submission`); }}
                            className="rounded-full border-white/15 text-xs h-7 px-3 shrink-0" data-testid={`amplify-target-${c.channel_id}`}>Target</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <p className="text-[11px] text-muted-foreground mt-5 leading-relaxed border-t border-white/5 pt-4">
                  Roster reach figures are projections. Shoutouts are arranged with partner creators — our team reviews each submission and updates your status here.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardShell>
  );
};

export default Amplify;
